import { useState, useEffect } from 'react';

interface TokenPrice {
  price: number;
  change24h: number;
}

interface TokenPricesHook {
  apePrice: TokenPrice | null;
  gemPrice: TokenPrice | null;
  isLoading: boolean;
  error: Error | null;
}

interface CoinGeckoResponse {
  apecoin: {
    usd: number;
    usd_24h_change: number;
  };
}

interface DexScreenerResponse {
  pairs: Array<{
    priceUsd: string;
    priceChange: {
      h24: number;
    };
  }>;
  pair: {
    priceUsd: string;
    priceChange: {
      h24: number;
    };
  };
}

export function useTokenPrices(): TokenPricesHook {
  const [apePrice, setApePrice] = useState<TokenPrice | null>(null);
  const [gemPrice, setGemPrice] = useState<TokenPrice | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchPrices() {
      try {
        // Fetch APE price from CoinGecko with retries
        let apeResponse = null;
        let retries = 3;
        
        while (retries > 0 && !apeResponse) {
          try {
            apeResponse = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=apecoin&vs_currencies=usd&include_24hr_change=true', {
              headers: {
                'Accept': 'application/json'
              }
            });
            
            if (!apeResponse.ok) {
              throw new Error(`APE price fetch failed: ${apeResponse.status}`);
            }
          } catch (err) {
            retries--;
            if (retries === 0) throw err;
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
        
        const apeData = await apeResponse.json() as CoinGeckoResponse;
        
        if (isMounted) {
          setApePrice({
            price: apeData.apecoin.usd,
            change24h: apeData.apecoin.usd_24h_change
          });
        }

        // Fetch GEM price from DEXScreener with retries
        let gemResponse = null;
        retries = 3;
        
        while (retries > 0 && !gemResponse) {
          try {
            gemResponse = await fetch('https://api.dexscreener.com/latest/dex/pairs/apechain/0x98f1748e9a0b72ecf5589500cb87b87d91c38a39', {
              headers: {
                'Accept': 'application/json'
              }
            });

            if (!gemResponse.ok) {
              throw new Error(`GEM price fetch failed: ${gemResponse.status}`);
            }
          } catch (err) {
            retries--;
            if (retries === 0) throw err;
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }

        const gemData = await gemResponse.json() as DexScreenerResponse;
        
        if (isMounted && gemData.pair) {
          setGemPrice({
            price: Number(gemData.pair.priceUsd),
            change24h: Number(gemData.pair.priceChange.h24)
          });
        }

        if (isMounted) {
          setError(null);
        }
      } catch (error) {
        console.error('Error fetching token prices:', error);
        if (isMounted) {
          setError(error as Error);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchPrices();
    const interval = setInterval(fetchPrices, 30000); // Update every 30 seconds

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  return {
    apePrice,
    gemPrice,
    isLoading,
    error
  };
}